// Spec 48 ai-ux — scope_indicator.
//
// "Persistent badge at the top of any AI thread panel affirming the
// current permission scope. Always visible while AI is active. Click
// opens a detailed scope panel listing entities, attributes, and
// policies in effect." (spec 48 line 1161)
//
// a11y per spec 48:
//   - Badge is role='status' with aria-live='polite'
//   - Expand toggle opens a side panel with focus trap; Esc returns
//     focus to the badge (focus trap is consumer's responsibility)
//   - Pulse on permission refresh is visual only; the associated system
//     bubble announces the change
//   - Icon + label — never icon only

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface ScopeIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  /** Override the default English label ("Answering within your HR scope"). */
  readonly label?: string;
  /** Optional context tail, e.g., "employee: Jamie L." */
  readonly contextSubject?: string;
  /** Decorative shield icon (renders aria-hidden). */
  readonly icon?: ReactNode;
  /** Click → consumer opens scope-detail side panel + focus trap. */
  readonly onExpand?: () => void;
  /** Tooltip text on the expand toggle (i18n). */
  readonly expandTooltip?: string;
  /** Visual pulse signal — set true briefly when permissions refresh; CSS owns the animation. */
  readonly pulsing?: boolean;
}

export const ScopeIndicator = forwardRef<HTMLDivElement, ScopeIndicatorProps>(
  (
    {
      label = "Answering within your HR scope",
      contextSubject,
      icon,
      onExpand,
      expandTooltip = "View scope details",
      pulsing = false,
      className,
      ...rest
    },
    ref,
  ) => {
    // Spec a11y: badge is role='status' aria-live='polite'. Pulse is
    // visual-only; the consumer's system-bubble renders the actual
    // verbal announcement (per spec rule line 1194).
    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        data-component="scope-indicator"
        data-pulsing={pulsing || undefined}
        className={cx(
          "nai-scope-indicator",
          pulsing && "nai-scope-indicator--pulsing",
          className,
        )}
        {...rest}
      >
        {icon !== undefined && (
          <span
            aria-hidden="true"
            data-component="scope-indicator-icon"
            className="nai-scope-indicator-icon"
          >
            {icon}
          </span>
        )}
        <span data-component="scope-indicator-label" className="nai-scope-indicator-label">
          {label}
        </span>
        {contextSubject !== undefined && (
          <>
            <span aria-hidden="true" className="nai-scope-indicator-separator">
              {" \u00b7 "}
            </span>
            <span
              data-component="scope-indicator-context"
              className="nai-scope-indicator-context"
            >
              {`context: ${contextSubject}`}
            </span>
          </>
        )}
        {onExpand !== undefined && (
          <button
            type="button"
            data-component="scope-indicator-expand"
            className="nai-scope-indicator-expand"
            aria-label={expandTooltip}
            onClick={onExpand}
          >
            {/* visual chevron via CSS pseudo or icon library — caller
                styles. Empty span keeps button non-empty for a11y. */}
            <span aria-hidden="true">›</span>
          </button>
        )}
      </div>
    );
  },
);
ScopeIndicator.displayName = "ScopeIndicator";
